import { ChatBotSuggestion } from '@/components/shared/ChatBot';
import { CreateCampaignInput, KPI, useCampaigns } from '@/hooks/useCampaigns';
import { useTeams } from '@/hooks/useTeams';
import {
  useCopilotAction,
  useCopilotChat,
  useCopilotReadable,
} from '@copilotkit/react-core';
import { Role, TextMessage } from '@copilotkit/runtime-client-gql';
import { RefObject, useEffect } from 'react';
import { SubmitHandler, UseFormSetValue } from 'react-hook-form';
import * as yup from 'yup';

export type CampaignSetupQuestions = CreateCampaignInput;

const agentName = 'Sam';

const formatKPIOption = (kpi: KPI) => {
  return kpi.charAt(0) + kpi.slice(1).toLowerCase();
};

const getSuggestedObjectives = (kpi: KPI, teamInfo: any) => {
  const baseObjectives = {
    [KPI.AWARENESS]: [
      'Increase brand visibility',
      'Expand social media reach',
      'Boost brand recognition',
      'Generate buzz around the brand',
    ],
    [KPI.CONSIDERATION]: [
      'Drive website traffic',
      'Increase engagement rates',
      'Build brand authority',
      'Generate quality leads',
    ],
    [KPI.INTEREST]: [
      'Increase newsletter signups',
      'Grow social media following',
      'Boost content engagement',
      'Generate qualified leads',
    ],
    [KPI.CONVERSIONS]: [
      'Drive product sales',
      'Increase service bookings',
      'Generate qualified leads',
      'Boost conversion rates',
    ],
  };

  return baseObjectives[kpi];
};

const conversationalQuestions = [
  {
    question: 'What would you like to name this campaign?',
    relatedFields: ['name'],
  },
  {
    question:
      'When would you like this campaign to start? (Please provide a date in DD/MM/YYYY format)',
    relatedFields: ['start_date'],
  },
  {
    question:
      'And when should the campaign end? (Please provide a date in DD/MM/YYYY format)',
    relatedFields: ['end_date'],
  },
  {
    question:
      'Are there any key milestone dates during the campaign we should keep in mind? For each date, please provide both the date and what it represents (e.g., "01/03/2024 - Pancake Day, 17/03/2024 - St. Patrick\'s Day"). Separate multiple entries with commas.',
    relatedFields: ['key_dates', 'key_date_contexts'],
  },
  {
    question: (teamInfo: any, kpi: KPI) => {
      const suggestions = getSuggestedObjectives(kpi, teamInfo)
        .slice(0, 3)
        .join('\n- ');

      return `What are your main objectives for this campaign? Here are some suggestions based on your ${formatKPIOption(kpi)} focus:

- ${suggestions}

Feel free to use these or share your own objectives (separate multiple objectives with commas).`;
    },
    relatedFields: ['objectives'],
  },
];

// New type for raw parameters from the chat
type RawCampaignSetupParameters = {
  name: string;
  start_date: string;
  end_date: string;
  key_dates: string;
  key_date_contexts: string;
  objectives: string;
  kpi: KPI;
};

// Parse function to convert raw parameters to CampaignSetupQuestions
const parseParameters = (
  raw: RawCampaignSetupParameters,
): CampaignSetupQuestions => {
  const parseDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('/').map(Number);

    return new Date(year, month - 1, day);
  };

  const start_date = parseDate(raw.start_date);
  const end_date = parseDate(raw.end_date);

  const key_dates =
    raw.key_dates === 'None'
      ? []
      : raw.key_dates.split(',').map((date, index) => {
          const contexts: string[] = raw.key_date_contexts.split(',');
          const context: string | undefined = contexts[index]?.trim();
          if (!context) {
            throw new Error(`Context is required for key date: ${date.trim()}`);
          }
          const parsedDate = parseDate(date.trim());

          // Validate date range
          if (parsedDate < start_date) {
            throw new Error(
              `Key date ${date.trim()} must be after start date ${raw.start_date}`,
            );
          }
          if (parsedDate > end_date) {
            throw new Error(
              `Key date ${date.trim()} must be before end date ${raw.end_date}`,
            );
          }

          return {
            date: parsedDate,
            context,
          };
        });

  return {
    name: raw.name,
    start_date,
    end_date,
    key_dates,
    objectives: raw.objectives.split(',').map((objective) => objective.trim()),
    kpi: raw.kpi,
  };
};

export const useCampaignSetupAgent = ({
  setValue,
  formValues,
  inputRef,
  validationSchema,
  handleSubmit,
}: {
  setValue: UseFormSetValue<CampaignSetupQuestions>;
  formValues: any;
  inputRef: RefObject<HTMLTextAreaElement>;
  validationSchema: yup.ObjectSchema<any>;
  handleSubmit: SubmitHandler<CampaignSetupQuestions>;
}) => {
  const { activeTeam } = useTeams();
  const { recommendedKpi } = useCampaigns();

  const {
    visibleMessages,
    appendMessage,
    setMessages,
    isLoading: isChatLoading,
  } = useCopilotChat({
    id: 'campaign-setup',
    makeSystemMessage:
      () => `Current Date (UTC): ${new Date().toISOString()} this date is the most important and every date they give you must check against the date of today, especially when the response is a date

You are ${agentName}, helping to set up a social media campaign with clear objectives and timelines.

Important Date Rules:
- Campaign start date must be today or in the future
- Campaign end date must be after the start date
- Any key milestone dates must fall between start and end dates
- All dates must be in DD/MM/YYYY format
- Each key date must have a context explaining what it represents (e.g., "01/03/2024 - Pancake Day")

Core Focus:
- Guide users through campaign setup with clarity
- Make data-driven suggestions based on the team's information
- Keep the conversation flowing naturally
- Help articulate meaningful campaign objectives
- Ensure key dates have meaningful context

Team Context:
- Use the provided Team Information context to understand the team's industry, audience, and brand voice
- Tailor suggestions and objectives to match their tone_and_style
- Consider their posting_frequency when discussing campaign timelines
- Ensure objectives align with their industry and target audience
- Reference their brand_voice in your communication style

Remember to:
- !IMPORTANT Ask only ONE question at a time
- Wait for the response before moving to the next question
- Keep follow-up questions natural and conversational
- Focus on one aspect at a time
- Actively use the team's context when suggesting objectives
- Make industry-specific suggestions based on their profile
- Make sure you validate the user's input against the format requirements every time they submit a response
- For key dates, ensure both the date and its context are provided
- CONFIRM SUBMISSION BEFORE SUBMITTING

Format Requirements:
- Start and end dates should be in DD/MM/YYYY format
- Key dates should be in format "DD/MM/YYYY - Context" and comma-separated
- Objectives should be comma-separated
- KPI should be one of: Awareness, Consideration, Interest, Conversions

When suggesting objectives:
1. Consider the team's industry: ${activeTeam.data?.industry}
2. Target their specific audience: ${activeTeam.data?.audience}
3. Match their brand voice: ${activeTeam.data?.brand_voice}
4. Align with their tone and style: ${activeTeam.data?.tone_and_style}

Please guide the user to provide information in these formats while keeping their brand context in mind`,
  });

  const getNextQuestion = (currentValues: Partial<CampaignSetupQuestions>) => {
    return conversationalQuestions.find((q) =>
      q.relatedFields.some(
        (field) => !currentValues[field as keyof CampaignSetupQuestions],
      ),
    );
  };

  const handleKPISelection = async (selectedKPI: KPI) => {
    // First set the form value
    setValue('kpi', selectedKPI);

    // Create both messages
    const userMessage = new TextMessage({
      content: `Continue with ${formatKPIOption(selectedKPI)}`,
      role: Role.User,
    });

    const nextQuestion = getNextQuestion(formValues);
    const assistantMessage = new TextMessage({
      content: `Great choice! You've selected ${formatKPIOption(selectedKPI)} as your KPI. 

${nextQuestion?.question || ''}`,
      role: Role.Assistant,
    });

    // Append messages sequentially but without delay
    await appendMessage(userMessage);
    await appendMessage(assistantMessage);
  };

  useCopilotReadable({
    description: 'Current campaign information',
    value: formValues,
  });

  useCopilotReadable({
    description: 'Team Information',
    value: activeTeam.data
      ? {
          name: activeTeam.data.name,
          industry: activeTeam.data.industry,
          audience: activeTeam.data.audience,
          tone_and_style: activeTeam.data.tone_and_style,
          brand_voice: activeTeam.data.brand_voice,
          posting_frequency: activeTeam.data.posting_frequency,
        }
      : null,
  });

  // @ts-expect-error - CopilotKit requires "moduleResolution": "bundler" in tsconfig.json,
  // but project uses "node". This causes type conflicts that can be safely ignored
  // as the runtime behavior is correct despite the type mismatch.
  useCopilotAction({
    name: 'CollectCampaignInfo',
    description: 'Collect campaign setup information',
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Name of the campaign',
        required: true,
      },
      {
        name: 'start_date',
        type: 'string',
        description: 'Start date in DD/MM/YYYY format, must be in the future',
        pattern: '^\\d{2}/\\d{2}/\\d{4}$',
        required: true,
      },
      {
        name: 'end_date',
        type: 'string',
        description: 'End date in DD/MM/YYYY format, must be after start date',
        pattern: '^\\d{2}/\\d{2}/\\d{4}$',
        required: true,
      },
      {
        name: 'key_dates',
        type: 'string',
        description:
          'Milestone dates in DD/MM/YYYY format, comma separated. Use "None" if no dates.',
      },
      {
        name: 'key_date_contexts',
        type: 'string',
        description:
          'Required context for each key date, comma separated. Must have same number of entries as key_dates.',
        required: true,
      },
      {
        name: 'objectives',
        type: 'string',
        description: 'Campaign objectives as comma-separated list',
        required: true,
      },
      {
        name: 'kpi',
        type: 'string',
        description: 'Primary KPI for the campaign',
        enum: Object.values(KPI),
        required: true,
      },
    ],
    handler: async (parameters: RawCampaignSetupParameters) => {
      try {
        const parsedParameters = parseParameters(parameters);
        const isValid = await validationSchema.isValid(parsedParameters);

        if (isValid) {
          await handleSubmit(parsedParameters);
        } else {
          const nextQuestion = getNextQuestion(parsedParameters);

          if (nextQuestion) {
            const questionContent =
              typeof nextQuestion.question === 'function'
                ? nextQuestion.question(activeTeam.data, parsedParameters.kpi)
                : nextQuestion.question;

            appendMessage(
              new TextMessage({
                content: `Let's be more specific. ${questionContent}`,
                role: Role.Assistant,
              }),
            );

            return;
          }

          // If we have all the required fields and validation passed, proceed with submission
          if (!nextQuestion) {
            Object.keys(parsedParameters).forEach((key) => {
              setValue(
                key as keyof CampaignSetupQuestions,
                parsedParameters[key as keyof CampaignSetupQuestions],
              );
            });
          }
        }
      } catch (error) {
        console.error('Error in handler:', error);
        if (error instanceof yup.ValidationError) {
          // Get the first error that matches our current question
          const currentQuestion = getNextQuestion(formValues);
          const relevantError = error.inner.find((err) =>
            currentQuestion?.relatedFields.includes(err.path as string),
          );

          if (relevantError) {
            const nextQuestion = getNextQuestion(formValues);
            if (nextQuestion) {
              const questionContent =
                typeof nextQuestion.question === 'function'
                  ? nextQuestion.question(activeTeam.data, formValues.kpi)
                  : nextQuestion.question;

              appendMessage(
                new TextMessage({
                  content: `I notice an issue: ${relevantError.message}. ${questionContent}`,
                  role: Role.Assistant,
                }),
              );
            }
          }
        }
      }
    },
  });

  useEffect(() => {
    if (!activeTeam.data || recommendedKpi.isLoading) return;

    const baseMessage = "## Let's set up your next campaign! 🎯\n\n";

    let messageContent = '';
    if (recommendedKpi.data?.kpi) {
      messageContent = `${baseMessage}Based on your brand profile and historical data, I recommend focusing on ${formatKPIOption(recommendedKpi.data.kpi)} as your main KPI.

${recommendedKpi.data.justification}

${recommendedKpi.data.tactic}

Please select your preferred KPI:`;
    } else {
      messageContent = `${baseMessage}Let's start by selecting a KPI (Key Performance Indicator) for your campaign. 

You can choose from:
- Awareness: Focus on reaching new audiences
- Consideration: Build interest in your brand
- Interest: Generate engagement and leads
- Conversions: Drive specific actions

Please select your preferred KPI:`;
    }

    setMessages([
      new TextMessage({
        content: messageContent,
        role: Role.System,
      }),
    ]);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    setMessages,
    activeTeam.data,
    recommendedKpi.data,
    recommendedKpi.isLoading,
    inputRef,
  ]);

  const suggestions: ChatBotSuggestion[] =
    formValues.kpi || recommendedKpi.isLoading
      ? []
      : recommendedKpi.data?.kpi
        ? [
            {
              text: `Continue with ${formatKPIOption(recommendedKpi.data.kpi)}`,
              action: () => handleKPISelection(recommendedKpi.data.kpi),
              variant: 'rainbow',
            },
            ...Object.values(KPI)
              .filter((kpi) => kpi !== recommendedKpi.data.kpi)
              .map((kpi) => ({
                text: `Switch to ${formatKPIOption(kpi)}`,
                action: () => handleKPISelection(kpi),
                variant: 'ghost' as const,
              })),
          ]
        : Object.values(KPI).map((kpi) => ({
            text: `Continue with ${formatKPIOption(kpi)}`,
            action: () => handleKPISelection(kpi),
            variant: 'ghost' as const,
          }));

  return {
    visibleMessages,
    appendMessage,
    isLoading: isChatLoading || recommendedKpi.isLoading,
    suggestions,
  };
};
